import { useCallback, useMemo } from 'react';

import { merge } from 'lodash-es';
import { useLocation } from 'react-router';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';

import {
	usePetitionEditBannerState,
	usePetitionEditPageData,
	usePetitionEditPageState,
} from '#src/app/pages/petitionEditV2/pageContext';
import { validateSchema } from '#src/app/pages/petitionEditV2/shared/schemas';
import type { ParsedErrors } from '#src/app/pages/petitionEditV2/shared/schemas';
import { isError, isLoaded, isLoading } from '#src/app/shared/utils/async';

import { MediaTypes } from '../shared/types';
import { useEditPublishedPetition } from '../useEditPublishedPetition';
import { AUTHENTICATION_EDIT_PUBLISHED_PETITION_ERROR } from '../useEditPublishedPetition/constants';
import type { PetitionInput } from '../useEditPublishedPetition/types';
import { useSaveImage } from '../useSaveImage';

type Field = keyof ParsedErrors;
type UseFormFooterReturn = ModelHookResult<{ isSubmitting: boolean }, { handleSubmit: () => Promise<void> }>;

// eslint-disable-next-line max-lines-per-function
export function useFormFooter(): UseFormFooterReturn {
	const [state, mutations] = usePetitionEditPageState();
	const { data } = usePetitionEditPageData();
	const { actions } = usePetitionEditBannerState();
	const { translate } = useI18n();
	const track = useTracking();
	const navigate = useNavigate();
	const { pathname, search } = useLocation();

	const { mutate: saveImage, ...saveImageState } = useSaveImage(state.uploader);
	const { mutate: handleEditPublishedPetition, ...editPublishedPetitionState } = useEditPublishedPetition({
		onSuccess: (_, { editedProperties }) => {
			// track success with changed fields
			void track('edit_petition_success', {
				changed_fields: editedProperties,
			});
			navigate(`/p/${encodeURIComponent(data.petition.slug)}/dashboard`);
		},
		onError: (error) => {
			void track('edit_petition_save_error', { error });

			if (error === AUTHENTICATION_EDIT_PUBLISHED_PETITION_ERROR) {
				const params = encodeURIComponent(`${pathname}${search}`);
				navigate(`/login_or_join?redirectTo=${params}`);
			} else {
				actions.addBanner({
					message: translate('corgi.petition.edit.errors.edit_page_save_error'),
					severity: 'critical',
					dismissable: true,
				});
			}
		},
	});

	const handleValidationError = useCallback(
		(errors: ParsedErrors) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const keys = Object.keys(errors) as Field[];
			keys.forEach((key) => {
				mutations.setFieldError(key, errors[key]);
			});
		},
		[mutations],
	);

	const handleSaveImage = useCallback(async () => {
		const result = await saveImage();

		if (isError(result)) {
			void track('edit_petition_media_upload_error', { error: result.error });
			return undefined;
		}

		const url = result.data.uploadURL ? unescape(result.data.uploadURL) : undefined;
		return url;
	}, [saveImage, track]);

	const handleSubmit = useCallback(async () => {
		void track('edit_petition_click_save_button');

		const validSchema = validateSchema(state);

		if (validSchema.status === 'error') {
			handleValidationError(validSchema.error);
			return;
		}

		// state.uploader is only populated if the user has selected a new image
		// only call handleSaveImage if uploader exists
		if (state.uploader) {
			const url = await handleSaveImage();
			if (url) {
				const withMediaData = merge({}, validSchema.data, {
					media: { croppedPhoto: { srcUrl: encodeURI(url), type: MediaTypes.USER } },
				});
				await handleEditPublishedPetition(withMediaData);
			}
		} else {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			await handleEditPublishedPetition(validSchema.data as PetitionInput);
		}
	}, [track, state, handleValidationError, handleEditPublishedPetition, handleSaveImage]);

	const isSubmitting = useMemo(() => {
		const isSubmittingImage = isLoading(saveImageState) || isLoaded(saveImageState);
		const isSubmittingPetition = isLoading(editPublishedPetitionState) || isLoaded(editPublishedPetitionState);
		return state.uploader ? isSubmittingImage || isSubmittingPetition : isSubmittingPetition;
	}, [editPublishedPetitionState, saveImageState, state.uploader]);

	return {
		data: {
			isSubmitting,
		},
		actions: {
			handleSubmit,
		},
	};
}
